import { ArrowForward } from '@mui/icons-material'
import { Box, Typography } from '@dtx-company/design-system/src'
import { EmptyObject } from 'type-fest'
import { FilledImageFieldImage, Slice } from '@prismicio/client'
import { Rating, Theme } from '@mui/material'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import { useBuyPlanPageContext } from '../../context/useBuyPlanPageContext'
import Image from 'next/image'
import Link from 'next/link'
import React, { useLayoutEffect, useMemo, useRef } from 'react'
import compact from 'lodash/compact'
import styled from '@emotion/styled'

type FilledTextSlice = Slice<'text', { text: string }>
type FilledLogoSlice = Slice<'logo', { image: FilledImageFieldImage }>
type FilledRatingSlice = Slice<'rating', { rating: number; link_label?: string; link_url?: string }>
type FilledDividerSlice = Slice<'divider', EmptyObject>

const _TextBox = styled(Box)``
const _LogoBox = styled(Box)``
const _RatingBox = styled(Box)``
const _DividerBox = styled(Box)``

const _RootBox = styled(Box)`
  align-items: center;
  background-color: ${({ theme }) => (theme as Theme).palette.container.info};
  color: ${({ theme }) => (theme as Theme).palette.primary.main};
  display: flex;
  flex-flow: row nowrap;
  gap: 8px 32px;
  min-height: 51px;
  overflow: auto;
  padding: 8px 32px;
  justify-content: center;

  .MuiRating-root {
    color: ${({ theme }) => (theme as Theme).brand.gold};
  }

  .MuiRating-iconEmpty {
    color: ${({ theme }) => (theme as Theme).brand.parchment};
  }

  ${_TextBox},
  ${_LogoBox},
  ${_RatingBox},
  ${_DividerBox} {
    display: flex;
    flex-flow: row nowrap;
    flex: 0 0 auto;
  }

  // NOTE: requires removing each element's display so that
  // the element is removed from the DOM and the layout shifts
  &.is-hidden {
    display: none;
  }
  &.is-hiding-logos ${_LogoBox} {
    display: none;
  }
  &.is-hiding-ratings ${_RatingBox} {
    display: none;
  }
  &.is-hiding-dividers ${_DividerBox} {
    display: none;
  }
`

export function BuyPlanPageBanner(): JSX.Element | null {
  const { document } = useBuyPlanPageContext()
  const rootBoxRef = useRef<HTMLDivElement>()

  // Need to filter the slices up-front or else there could be a disconnect where there
  // technically is a certain type of slice, but it doesn't render, and so the responsive
  // hook should exclude it
  const slices = useMemo(() => {
    return compact(
      (document.data.body || []).map(slice => {
        switch (slice.slice_type) {
          case 'text':
            if (slice.primary.text) {
              return slice as FilledTextSlice
            }
            break
          case 'logo':
            if (slice.primary.image?.url) {
              return slice as FilledLogoSlice
            }
            break
          case 'rating':
            const rating = slice.primary.rating ? parseFloat(slice.primary.rating) : undefined
            if (rating && !isNaN(rating) && rating >= 0 && rating <= 5) {
              return { ...slice, primary: { ...slice.primary, rating } } as FilledRatingSlice
            }
            break
          case 'divider':
            return slice as FilledDividerSlice
        }
        return undefined
      })
    )
  }, [document.data.body])

  // Handle responsiveness as the window resizes
  // Works by managing a list of css classes that will hide elements, applying
  // enough of the classes that enough items are hidden that the remaining items
  // fit in the screen resolution
  useLayoutEffect(() => {
    if (!slices.length) return

    // Determine which types of items are present
    const hasText = Boolean(slices.find(slice => slice.slice_type === 'text'))
    const hasLogo = Boolean(slices.find(slice => slice.slice_type === 'logo'))
    const hasRating = Boolean(slices.find(slice => slice.slice_type === 'rating'))
    const hasDivider = Boolean(slices.find(slice => slice.slice_type === 'divider'))

    function getRootBox(): HTMLDivElement | undefined {
      return rootBoxRef.current
    }

    function measure(): void {
      const rootBox = getRootBox()
      if (!rootBox) return

      // Define the order that things should be hidden, in reverse
      const sequence = (() => {
        const seq = ['is-hidden']
        if (hasText) seq.push('is-hiding-text')
        if (hasRating) seq.push('is-hiding-ratings')
        if (hasLogo) seq.push('is-hiding-logos')
        if (hasDivider) seq.push('is-hiding-dividers')

        // Remove the second to last item so that the entire banner will be hidden,
        // when the last item is hidden; otherwise, it'll display the banner
        // but nothing in it, and that empty banner will always fit and never hide
        return [seq[0], ...seq.slice(2)]
      })()

      // Whenever the browser resizes, start by removing all classes (showing all items)
      // so that if they all fit after the resize then they'll all be visible
      const classList = rootBox.classList
      sequence.forEach(item => {
        classList.remove(item)
      })

      // Once they're all visible, check the size of the container and keep adding
      // a class (to remove a set of items) one at a time until enough items are removed
      // that the remaining items are all visible.
      // If the last remaining item still doesn't fit then the entire banner is hidden.
      // Couples things to note:
      // - this all happens in a single render loop, but does recalculate DOM
      //   dimensions each time a class is added and the dimensions are re-checked.
      // - once the parent element itself is hidden, it's width will be 0 so the
      //   loop will stop
      while (rootBox.offsetWidth > 0 && rootBox.scrollWidth > rootBox.offsetWidth) {
        const selectedItem = sequence.find((item, index) => {
          if (classList.contains(sequence[index + 1]) || index === sequence.length - 1) {
            return true
          }
        })
        classList.add(selectedItem || '')
      }
    }

    let animationFrameId: number | undefined = undefined
    function start(): void {
      const rootBox = getRootBox()
      if (rootBox && rootBox.offsetWidth > 0) {
        measure()
      } else {
        animationFrameId = window.requestAnimationFrame(start)
      }
    }

    start()
    window.addEventListener('resize', measure)

    return () => {
      window.removeEventListener('resize', measure)
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId)
      }
    }
  }, [slices])

  // Hide the banner if there are no slices
  if (!slices.length) return null

  return (
    <_RootBox ref={rootBoxRef} data-testid="BuyPlanPageBanner">
      {slices.map(slice => {
        switch (slice.slice_type) {
          case 'text':
            return (
              <_TextBox key={slice.id} data-testid="BuyPlanPageBanner_TextBox">
                <Typography component="span" variant="subtitleSmall">
                  {slice.primary.text}
                </Typography>
              </_TextBox>
            )
          case 'logo':
            return (
              <_LogoBox key={slice.id} data-testid="BuyPlanPageBanner_LogoBox">
                <Image
                  key={slice.id}
                  src={slice.primary.image.url}
                  alt={slice.primary.image.alt || ''}
                  height={slice.primary.image.dimensions.height}
                  width={slice.primary.image.dimensions.width}
                />
              </_LogoBox>
            )
          case 'rating':
            const rating = slice.primary.rating
            return (
              <_RatingBox key={slice.id} data-testid="BuyPlanPage_RatingBox">
                <Rating defaultValue={rating} precision={0.5} readOnly />
                <span style={{ paddingLeft: '16px' }}>
                  <Typography component="span" variant="subtitleSmall">
                    {rating}/5
                  </Typography>
                </span>
                {slice.primary.link_label && slice.primary.link_url && (
                  <span style={{ paddingLeft: '16px' }}>
                    <Typography component="span" variant="subtitleSmall">
                      <Link
                        href={slice.primary.link_url}
                        target="_blank"
                        style={{
                          color: 'inherit',
                          textDecoration: 'none',
                          display: 'flex',
                          flexFlow: 'row nowrap',
                          alignItems: 'center'
                        }}
                        onClick={() => {
                          fireAnalyticsEvent('BuyPlanPage_Rating_Clicked')
                        }}
                      >
                        <span style={{ textDecoration: 'underline' }}>
                          {slice.primary.link_label}
                        </span>
                        <ArrowForward sx={{ fontSize: '0.8em', marginLeft: '4px' }} />
                      </Link>
                    </Typography>
                  </span>
                )}
              </_RatingBox>
            )
          case 'divider':
            return (
              <_DividerBox key={slice.id} data-testid="BuyPlanPage_DividerBox">
                <Box
                  sx={{
                    height: '19px',
                    width: '1px',
                    backgroundColor: 'currentColor'
                  }}
                />
              </_DividerBox>
            )
        }
      })}
    </_RootBox>
  )
}
